<template>
  <div class="gmap-zone" :class="{ 'gmap-zone__disabled-event': !displayDetailsModal }">
    <div class="gmap-zone__box">
      <header class="gmap-zone__box__header">
        <ui-button
          v-if="displayDetailsModal"
          class="gmap-zone__box__header__close"
          @click.prevent="setCurrentZone(null)"
          button="secondary"
          :label="$t('commons.buttons.close')"
          :icon-only="true"
          icon="close"
        />
        <div class="gmap-zone__box__header__marker">
          <marker-default class="gmap-zone__box__header__marker__img" :filters="filters" :current-value="zone.value" />
        </div>
        <div class="gmap-zone__box__header__title">
          {{ zone.name[$i18n.locale] || zone.name.default }}
        </div>
      </header>

      <div v-if="!isLoading && displayDetailsModal" class="gmap-zone__box__container">
        <div :style="{ backgroundColor: colorGenerate() }" class="gmap-zone__box__container__title">
          {{ $t('gmap.zoneIntention.title') }}
        </div>
        <div class="gmap-zone__box__container__subtitle">
          {{
            this.$t('gmap.zoneIntention.intentionsTotal', {
              count: detailsTotalCount,
            })
          }}
        </div>
        <ui-chart class="gmap-zone__box__container__chart" id="chart-zone-intention" :options="options" />
      </div>

      <footer class="gmap-zone__box__footer">
        <ui-button
          class="gmap-zone__box__footer__close"
          :label="$t('commons.buttons.close')"
          button="primary"
          @click.prevent="setCurrentZone(null)"
        />
      </footer>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import MarkerDefault from '@/components/Marker/Default.vue'
import UiButton from '@/components/UI/Button.vue'
import { launchGAEvent } from '@/utils/gtm.util'
import { numberFormat } from '@/utils/formatters.util'
import { generateColors } from '@/utils/colors.util'
import UiChart from '@/components/UI/Chart.vue'

export default {
  name: 'GmapZoneIntention',
  components: {
    UiButton,
    MarkerDefault,
    UiChart,
  },
  props: {
    zone: {
      type: Object,
      required: true,
    },
    location: {
      type: Object,
      required: true,
    },
    filters: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      isLoading: true,
      chart: {},
      detailsTotalCount: 0,
      options: {
        tooltip: {
          trigger: 'item',
          backgroundColor: 'rgba(255, 255, 255)',
          textStyle: {
            color: 'rgba(0, 0, 0)',
          },
          padding: 10,
          confine: true,
          extraCssText: 'min-width: 200px; box-shadow: 1px 2px 6px rgba(0,0,0, 0.2);',
          formatter: series =>
            `<span style="text-transform: capitalize; font-size:12px; font-weight:700;">${series.data.source} - ${
              series.data.percentage
            }%</span>
              <br />
              <div style="font-size:17px; font-weight:900; text-align:center; margin: 10px 0; line-height: 1;">
                ${numberFormat({
                  number: series.data.value,
                  style: 'currency',
                  currency: 'USD',
                })}
                <br />
                <span style="font-size:11px; font-weight:500;">${this.$t('gmap.zoneIntention.intention', {
                  count: series.data.count,
                })}</span>
              </div>
            `,
        },
        graphic: {
          elements: [
            {
              type: 'text',
              left: 'center',
              top: 'middle',
              z: 999,
              style: {
                textAlign: 'center',
                fontSize: '18',
                fontWeight: '800',
                color: 'rgba(0,0,0)',
              },
            },
          ],
        },
        series: [
          {
            type: 'pie',
            radius: ['55%', '85%'],
            label: {
              show: false,
            },
            color: ['#73C0DE', '#5470C6', '#91CC75', '#FAC858', '#EE6666', '#e60270', '#404040', '#a002f5'],
            itemStyle: {
              borderColor: '#fff',
              borderWidth: 3,
            },
            emphasis: {
              label: {
                show: false,
              },
            },
            labelLine: {
              show: false,
            },
            data: [],
          },
        ],
      },
    }
  },
  watch: {
    zone: {
      deep: true,
      handler() {
        this.isLoading = true
        this.generateData()
      },
    },
  },
  mounted() {
    this.generateData()
  },
  computed: {
    displayDetailsModal() {
      return Object.keys(this.location).length > 0
    },
  },
  methods: {
    ...mapActions({
      setCurrentZone: 'data/setCurrentZone',
    }),
    colorGenerate() {
      return generateColors(this.zone.value, this.filters)
    },
    generateData() {
      this.detailsTotalCount = 0
      if (this.displayDetailsModal) {
        this.options.series[0].data = this.location.details
        this.options.series[0].data.forEach(element => {
          this.detailsTotalCount += element.count
          element.percentage = Math.round((element.value / this.location.value) * 100)
        })
        this.options.graphic.elements[0].style.text = `${numberFormat({
          number: this.location.value,
          style: 'currency',
          currency: 'USD',
        })}`
      }

      this.isLoading = false
    },
    onClickSetCurrentZone() {
      launchGAEvent(this.$route.params.id, 'see_locations_in_area', 'show')
    },
  },
}
</script>

<style lang="scss" scoped>
.gmap-zone {
  @include wrapper(flex-start, $gutter-md 0 $gutter-md $gutter-md);

  @media (min-width: $screen-sm) {
    right: initial;
    bottom: initial;
    width: calc(#{$gmap-sidebar-width-tablet} + #{$gutter-md});
  }

  @media (min-width: $screen-lg) {
    width: calc(#{$gmap-sidebar-width-desktop} + #{$gutter-md});
  }

  &__disabled-event {
    pointer-events: none;
    cursor: inherit;
  }

  &__box {
    @include box;

    width: $gmap-sidebar-width-mobile;

    @media (min-width: $screen-sm) {
      width: $gmap-sidebar-width-tablet;
    }

    @media (min-width: $screen-lg) {
      width: $gmap-sidebar-width-desktop;
    }

    &__header {
      @include header;

      &__close {
        @include close('top');
      }

      &__marker {
        margin-right: 12px;
        width: 20px;
      }

      &__title {
        @include title(1rem);

        display: -webkit-box;
        flex: 1;
        padding-bottom: 1px;
        overflow: hidden;
        text-overflow: ellipsis;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
      }
    }

    &__container {
      @include container;

      &__title {
        font-weight: 700;
        font-size: 0.9rem;
        width: max-content;
        color: white;
        border-radius: 5px;
        padding: 8px 8px;
      }

      &__subtitle {
        margin-top: 4px;
        font-size: 0.8rem;
      }

      &__chart {
        height: 300px;
      }
    }

    &__footer {
      @include footer;

      @media (min-width: $screen-sm) {
        display: none;
      }
    }
  }
}
</style>
